import React, {useEffect, useState, useRef} from 'react';
import {Skeleton, Button, Space, Typography, Spin} from 'antd';
import {EditOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import fetchDocument from '../../utils/document/fetchDocument';
import updateUserProfile from '../../utils/user/updateUserProfile';
import { getCurrentUser } from '../../utils/userStorage';
import CopyToClipboardButton from '../../components/buttons/CopyToClipboardButton';
import DownloadPDFButton from '../../components/buttons/DownloadPDFButton';
import PatientDocumentMarkdown from "./PatientDocumentMarkdown";
import PatientDocumentEditor from "./PatientDocumentEditor";
import SignatureModal from './SignatureModal';
import SignatureButton from './SignatureButton';
import ThumbsRating from '../ThumbsRating';
import { toast } from 'react-hot-toast';

const {Text} = Typography;

export default function PatientDocumentViewer({document, onReloadDocuments}) {
  const {t} = useTranslation();
  const [currentDocument, setCurrentDocument] = useState(document);
  const [loading, setLoading] = useState(!document.isReady());
  const [editing, setEditing] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const pollingRef = useRef(null);
  const pdfRef = useRef();
  const currentUser = getCurrentUser();
  const hasSignature = currentUser?.profile?.signature_url;

  useEffect(() => {
    if (document.isReady()) {
      setLoading(false);
      return;
    }

    const poll = async () => {
      try {
        const updated = await fetchDocument(document.getUUID());
        setCurrentDocument(updated);
        if (updated.isReady()) {
          setLoading(false);
          clearInterval(pollingRef.current);
          onReloadDocuments?.();
        }
      } catch (error) {
        console.error('Failed to poll document', error);
        clearInterval(pollingRef.current);
      }
    };

    pollingRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollingRef.current);
  }, [document, onReloadDocuments]);

  const handleEditSave = (updatedDocument) => {
    setCurrentDocument(updatedDocument);
    setEditing(false);
    onReloadDocuments?.();
  };

  const handleSignatureSave = async (signatureData) => {
    setSavingSignature(true);
    try {
      const response = await updateUserProfile({ signature: signatureData });
      if (!response) {
        throw new Error('No response from server');
      }
      toast.success(t('patient_documents.signature.saved'));
      setShowSignatureModal(false);
      onReloadDocuments?.();
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error(t('patient_documents.signature.error'));
    } finally {
      setSavingSignature(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="skeleton">
        <Skeleton active paragraph={{rows: 10}}/>
      </div>
    );
  }

  const body = currentDocument.getBody();

  return (
    <div
      className="patient-document-viewer"
      style={{ marginRight: 18, marginLeft: 18, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative'}}>
      {!editing && (
        <Space style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}} wrap>
          <Button icon={<EditOutlined/>} onClick={() => setEditing(!editing)}>
            {t('common.edit')}
          </Button>
          <CopyToClipboardButton textToCopy={body} disabled={!body}/>
          <DownloadPDFButton pdfRef={pdfRef} eventCategory="PatientDocument" disabled={!body}/>
        </Space>
      )}

      <div ref={pdfRef} style={{flex: 1, overflow: 'auto'}}>
        {editing ? (
          <PatientDocumentEditor
            document={currentDocument}
            onSave={handleEditSave}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <PatientDocumentMarkdown 
            content={body}
            signatureUrl={hasSignature ? currentUser.profile.signature_url : null}
          />
        )}
      </div>

      {!editing && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '1.3em' 
        }}>
          <ThumbsRating 
            rateableUuid={currentDocument.getUUID()} 
            rateableType="Document"
          />
          <SignatureButton 
            onClick={() => setShowSignatureModal(true)}
            hasSignature={hasSignature}
          />
        </div>
      )}

      <SignatureModal
        open={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSave={handleSignatureSave}
        loading={savingSignature}
      />
    </div>
  );
}